/**
 * Header Component
 * Top navigation bar with glassmorphism styling and page navigation
 * UI/UX Enhancement: Added glass effect, improved tab indicator, smooth transitions
 */

import React from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, Tooltip, Tabs, Tab } from '@mui/material';
import { usePreferences } from '../context/PreferencesContext';
import Brightness4 from '@mui/icons-material/Brightness4';
import Brightness7 from '@mui/icons-material/Brightness7';
import type { PageType } from '../App';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../design-system';

interface HeaderProps {
  currentPage?: PageType;
  onPageChange?: (page: PageType) => void;
}

const PAGE_OPTIONS: Array<{ value: PageType; label: string }> = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'incidents', label: 'Incidents' },
];

export const Header: React.FC<HeaderProps> = ({ currentPage = 'dashboard', onPageChange }) => {
  const { preferences, toggleDarkMode } = usePreferences();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleTabChange = (_: React.SyntheticEvent, newValue: PageType) => {
    onPageChange?.(newValue);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        borderBottom: `1px solid ${isDark 
          ? tokens.colors.glass.dark.border 
          : tokens.colors.semantic.border}`,
        bgcolor: isDark 
          ? 'rgba(15, 23, 42, 0.85)' 
          : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        color: isDark ? tokens.colors.dark.text : tokens.colors.semantic.text,
        transition: `all ${tokens.motion.duration.normal}ms ${tokens.motion.easing.easeInOut}`,
      }}
    >
      <Toolbar sx={{ minHeight: 64 }}>
        {/* Logo with Icon */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            mr: 2,
          }}
        >
          <Box
            component="img"
            src="/apple-icon-60x60.png"
            alt="Netra"
            sx={{
              width: 36,
              height: 36,
              borderRadius: tokens.radius.sm,
              boxShadow: tokens.shadows.glass.sm,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '18px',
              background: tokens.colors.gradients.primary,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em',
            }}
          >
            Netra
          </Typography>
        </Box>

        {/* Navigation Tabs */}
        <Tabs
          value={currentPage}
          onChange={handleTabChange}
          component="nav"
          aria-label="Main navigation"
          sx={{ 
            flex: 1,
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: tokens.colors.gradients.primary,
            },
          }}
          textColor="inherit"
        >
          {PAGE_OPTIONS.map((page) => (
            <Tab
              key={page.value}
              value={page.value}
              label={page.label}
              sx={{ 
                textTransform: 'none', 
                fontSize: '0.9rem',
                fontWeight: 500,
                minHeight: 64,
                px: 2,
                color: isDark ? tokens.colors.gray[400] : tokens.colors.gray[600],
                transition: `all ${tokens.motion.duration.fast}ms ${tokens.motion.easing.easeOut}`,
                '&.Mui-selected': {
                  color: isDark ? tokens.colors.gray[0] : tokens.colors.gray[900],
                  fontWeight: 600,
                },
                '&:hover': {
                  color: isDark ? tokens.colors.gray[200] : tokens.colors.gray[900],
                  backgroundColor: isDark 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(0, 0, 0, 0.03)',
                },
              }}
            />
          ))}
        </Tabs>

        {/* Theme Toggle */}
        <Tooltip 
          title={preferences.darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          arrow
        >
          <IconButton 
            onClick={toggleDarkMode} 
            size="small"
            aria-label={preferences.darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            sx={{
              ml: 1,
              p: 1,
              borderRadius: tokens.radius.md,
              color: isDark ? tokens.colors.gray[400] : tokens.colors.gray[600],
              transition: `all ${tokens.motion.duration.fast}ms ${tokens.motion.easing.easeOut}`,
              '&:hover': {
                backgroundColor: isDark 
                  ? 'rgba(255, 255, 255, 0.1)' 
                  : 'rgba(0, 0, 0, 0.05)',
                color: isDark ? tokens.colors.gray[200] : tokens.colors.gray[900],
              },
            }}
          >
            {preferences.darkMode ? (
              <Brightness7 fontSize="small" />
            ) : (
              <Brightness4 fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
